import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import styles from './EventDropdown.module.css';
import { fetchAPI } from '../utils/api';
import EventLogo from './EventLogo';

interface Event {
  event_key: string;
  event_name: string;
}

interface EventDropdownProps {
  onSelect: (eventKey: string) => void;
  selectedEventKey?: string;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}

const EventDropdown: React.FC<EventDropdownProps> = ({ onSelect, selectedEventKey, isOpen, onToggle }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await fetchAPI('/events');
        setEvents(data.events);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to load events');
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents = searchTerm
    ? events
        .filter(event =>
          event.event_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.event_name.localeCompare(b.event_name))
    : events.sort((a, b) => a.event_name.localeCompare(b.event_name));

  const selectedEvent = events.find(event => event.event_key === selectedEventKey);

  const handleDropdownToggle = (isOpen: boolean) => {
    onToggle(isOpen);
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredEvents.length > 0) {
      onSelect(filteredEvents[0].event_key);
      onToggle(false);
    }
  };

  const handleToggle = (isOpen: boolean) => {
    if (isOpen && window.innerWidth <= 768) {
      const toggle = document.querySelector('.dropdown-toggle');
      const menu = document.querySelector('.dropdown-menu');
      if (toggle && menu) {
        const rect = toggle.getBoundingClientRect();
        (menu as HTMLElement).style.top = `${rect.bottom}px`;
      }
    }
  };

  const handleSelect = (eventKey: string) => {
    onSelect(eventKey);
    onToggle(false);
  };

  return (
    <Dropdown 
      className={styles.eventDropdown} 
      onToggle={handleDropdownToggle}
      show={isOpen}
    >
      <Dropdown.Toggle variant="outline-secondary" id="dropdown-event" className={`${styles.noCaretToggle} ${styles.dropdownToggle}`}>
        {selectedEvent ? (
          <>
            <EventLogo eventKey={selectedEvent.event_key} logoSize={16} />
            <span>{selectedEvent.event_name}</span>
          </>
        ) : selectedEventKey === '' ? (
          'Any'
        ) : (
          'Events'
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className={styles.dropdownMenu}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search events..."
            className={styles.searchInput}
            ref={searchInputRef}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            value={searchTerm}
          />
        </div>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={() => handleSelect('')}
          active={selectedEventKey === ''}
          className={styles.dropdownItem}
        >
          Any
        </Dropdown.Item>
        <Dropdown.Divider />
        {isLoading && <Dropdown.Item>Loading...</Dropdown.Item>}
        {error && <Dropdown.Item className={styles.error}>{error}</Dropdown.Item>}
        {filteredEvents.map((event) => (
          <Dropdown.Item
            key={event.event_key}
            onClick={() => handleSelect(event.event_key)}
            active={event.event_key === selectedEventKey}
            className={styles.dropdownItem}
          >
            <EventLogo eventKey={event.event_key} logoSize={16} />
            <span className={styles.eventName}>{event.event_name}</span>
          </Dropdown.Item>
        ))}
        {!isLoading && !error && filteredEvents.length === 0 && (
          <Dropdown.Item disabled>No events found</Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default EventDropdown;
